import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import logger from '../utils/logger';

const MaintenanceContext = createContext();

export const useMaintenanceMode = () => {
  const context = useContext(MaintenanceContext);
  if (!context) {
    throw new Error('useMaintenanceMode must be used within a MaintenanceProvider');
  }
  return context;
};

export const MaintenanceProvider = ({ children }) => {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const checkMaintenanceStatus = useCallback(async () => {
    try {
      const response = await api.get('/system/maintenance-status');
      if (response.success) {
        setIsMaintenanceMode(response.data.isMaintenanceMode);
        setLastUpdated(response.data.updatedAt);
      }
    } catch (error) {
      // If we can't reach the server, don't block the user
      logger.error('Error checking maintenance status:', error);
      setIsMaintenanceMode(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Check maintenance status on mount and periodically
  useEffect(() => {
    checkMaintenanceStatus();

    // Check every 30 seconds
    const interval = setInterval(checkMaintenanceStatus, 30000);

    return () => clearInterval(interval);
  }, [checkMaintenanceStatus]);

  // Toggle maintenance mode (admin only)
  const toggleMaintenanceMode = async (enabled) => {
    try {
      const response = await api.post('/system/maintenance-mode', { enabled });
      if (response.success) {
        setIsMaintenanceMode(response.data.isMaintenanceMode);
        setLastUpdated(response.data.updatedAt);
        return { success: true, data: response.data };
      }
      return { success: false, message: response.message };
    } catch (error) {
      logger.error('Error toggling maintenance mode:', error);
      return { success: false, message: error.message || 'Failed to toggle maintenance mode' };
    }
  };

  const value = {
    isMaintenanceMode,
    loading,
    lastUpdated,
    checkMaintenanceStatus,
    toggleMaintenanceMode
  };

  return (
    <MaintenanceContext.Provider value={value}>
      {children}
    </MaintenanceContext.Provider>
  );
};

export default MaintenanceContext;

